<script setup lang="ts">
import { computed, ref } from "vue";
import { Z_INDEX_TOOLBAR } from "./constants";
import CanvasButton from "../CanvasButton.vue";
import { Facade } from "@/drawing";
import type { DRDrawing, DRPartialProject } from "@/types";
import SaveBadge from "../SaveBadge.vue";
import SaveReminderBadge from "../SaveReminderBadge.vue";
import LegendSelector from "../Defaults/LegendSelector.vue";
import CanvasFileMenu from "../header/CanvasFileMenu.vue";
import ScaleSelector from "../header/ScaleSelector.vue";
import CatalogueTabs from "../header/CatalogueTabs.vue";
import CatalogueHelpDialog from "../header/CatalogueHelpDialog.vue";
import LegendChipContent from "../header/LegendChipContent.vue";
import ScaleChipContent from "../header/ScaleChipContent.vue";
import ScaleMenuState from "@/stores/ScaleMenuState";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import {
  areCatalogueTabsWrapped,
  headerHeight,
} from "@/stores/CanvasLayoutState";
import { useCanvasHeaderLayout } from "@/composable/useCanvasHeaderLayout";
import { trackTool, trackFeature } from "@/analytics/drawing-analytics";

/**
 * En-tête de l'outil dessin, sur une ligne :
 * menu fichier | légende, échelle | onglets des catalogues | annuler, rétablir, sauvegarde.
 * Quand les onglets n'y tiennent pas, ils passent seuls sur une 2e ligne (la
 * légende et l'échelle restent sur la 1re). Quand même le nom du plan n'a plus
 * la place, la légende et l'échelle se replient en un seul bouton.
 */
const props = defineProps<{
  drawingId: number;
  fullscreen: boolean;
  file?: DRDrawing;
  project?: DRPartialProject;
}>();

const emits = defineEmits<{
  (e: "update:fullscreen", value: boolean): void;
}>();

const isFullscreen = computed({
  get: () => props.fullscreen,
  set: (value) => {
    trackFeature("fullscreen", { value });
    emits("update:fullscreen", value);
  },
});

const activeLegendName = computed(
  () => CanvasSettingsState.activeLegend?.name || "-",
);

const showHelp = ref(false);

function undo() {
  if (Facade.isInitialized) {
    // Only counted when something was actually undone.
    if (Facade.history.undo()) trackTool("undo", "menu");
  }
}

function redo() {
  if (Facade.isInitialized) {
    if (Facade.history.redo()) trackTool("redo", "menu");
  }
}

// ── Mise en page automatique (légende repliée, onglets en 2e ligne) ─────────
const headerEl = ref<HTMLElement | null>(null);
const tabsSlot = ref<HTMLElement | null>(null);
const fileArea = ref<HTMLElement | null>(null);
const legendExpandedProbe = ref<HTMLElement | null>(null);
const legendCompactProbe = ref<HTMLElement | null>(null);
const { isLegendCompact } = useCanvasHeaderLayout({
  header: headerEl,
  tabsSlot,
  fileArea,
  legendExpandedProbe,
  legendCompactProbe,
});
</script>

<template>
  <div
    v-if="isFullscreen"
    class="position-absolute top-0 end-0 canvas-icon-scaled"
    :style="{ zIndex: Z_INDEX_TOOLBAR }"
  >
    <CanvasButton
      class="m-2 capture canvas-header-btn"
      icon="fullscreen-exit"
      @click="isFullscreen = false"
      :tooltipText="$t('canvas:tools.fullscreen.exit.tooltip.text')"
      :tooltipTitle="$t('canvas:tools.fullscreen.exit.tooltip.title')"
    />
  </div>

  <header
    v-else
    class="position-absolute no-capture top-0 start-0 w-100 canvas-text-scaled"
    :style="{ zIndex: Z_INDEX_TOOLBAR + 1 }"
  >
    <div
      ref="headerEl"
      class="w-100 capture canvas-header"
      :style="{ height: `${headerHeight}px` }"
    >
      <div class="header-row">
        <!-- Menu fichier : toujours tout à gauche -->
        <div ref="fileArea" class="header-file">
          <CanvasFileMenu
            :drawing-id="drawingId"
            :file="file"
            :project="project"
            @fullscreen="isFullscreen = true"
          />
        </div>

        <div class="canvas-divider-vertical flex-shrink-0" />

        <!-- Légende et échelle : toujours sur la 1re ligne. Faute de place,
             un seul bouton (icône) ouvre une popup qui réunit les deux. -->
        <div ref="legendArea" class="header-legend">
          <!-- Un seul sélecteur, dont seul le contenu change : basculer de
               mode ne referme pas la popup ouverte. -->
          <LegendSelector :with-scale="isLegendCompact">
            <button
              type="button"
              class="canvas-header-chip"
              :aria-label="`${$t(
                isLegendCompact
                  ? 'canvas:header.legend.compactLabel'
                  : 'canvas:header.legend.label',
              )} : ${activeLegendName}`"
            >
              <LegendChipContent
                :name="activeLegendName"
                :compact="isLegendCompact"
              />
            </button>
          </LegendSelector>
          <ScaleSelector v-if="!isLegendCompact" />
        </div>

        <div
          v-if="!areCatalogueTabsWrapped"
          class="canvas-divider-vertical flex-shrink-0"
        />

        <!-- Onglets des catalogues, ou place libre quand ils sont en 2e ligne -->
        <div ref="tabsSlot" class="header-tabs-slot">
          <CatalogueTabs
            v-if="!areCatalogueTabsWrapped"
            @help="showHelp = true"
          />
        </div>

        <!-- Annuler, rétablir, sauvegarde : toujours tout à droite -->
        <div class="header-actions">
          <CanvasButton
            size="sm"
            class="header-action-btn"
            icon="arrow-counterclockwise"
            @click="undo()"
            :tooltipText="$t('canvas:tools.undo.tooltip.text')"
            :tooltipTitle="$t('Undo')"
          />
          <CanvasButton
            size="sm"
            class="header-action-btn"
            icon="arrow-clockwise"
            @click="redo()"
            :tooltipText="$t('canvas:tools.redo.tooltip.text')"
            :tooltipTitle="$t('Redo')"
          />
          <div class="canvas-divider-vertical mx-1" />
          <SaveBadge class="header-save" />
          <!-- « Pensez à sauvegarder » / « Sauvegarde auto désactivée » : à
               gauche du groupe, par-dessus les onglets. Hors du flux, son
               apparition minutée ne fait ni basculer les onglets sur une
               2e ligne ni bouger le plan (cf. useCanvasHeaderLayout). -->
          <div class="header-save-reminder">
            <SaveReminderBadge />
          </div>
        </div>
      </div>

      <div v-if="areCatalogueTabsWrapped" class="header-row header-row-tabs">
        <CatalogueTabs class="flex-grow-1" @help="showHelp = true" />
      </div>

      <!-- Copies invisibles du groupe Légende + Échelle, déplié et replié :
           la place qu'il prendrait se mesure même quand il est replié. -->
      <div class="legend-probes" aria-hidden="true">
        <div ref="legendExpandedProbe" class="header-legend">
          <span class="canvas-header-chip">
            <LegendChipContent :name="activeLegendName" :compact="false" />
          </span>
          <span v-if="ScaleMenuState.scales.length" class="canvas-header-chip">
            <ScaleChipContent />
          </span>
        </div>
        <div ref="legendCompactProbe" class="header-legend">
          <span class="canvas-header-chip">
            <LegendChipContent :name="activeLegendName" :compact="true" />
          </span>
        </div>
      </div>
    </div>
  </header>

  <CatalogueHelpDialog v-model="showHelp" />
</template>

<style lang="scss" scoped>
.canvas-header {
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--canvas-bg);
  border-bottom: 1px solid var(--canvas-border);
  box-sizing: border-box;
}

// Les lignes se partagent la hauteur de l'en-tête (cf. CanvasLayoutState).
.header-row {
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
}

.header-row-tabs {
  border-top: 1px solid var(--canvas-border);
}

// Le nom du plan se tronque en premier : les autres groupes gardent leur taille.
.header-file {
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  padding: 0 4px;
  overflow: hidden;
}

.header-legend {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  padding: 0 4px;
}

// Base nulle : sa largeur ne dépend jamais des onglets qu'il contient.
.header-tabs-slot {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  overflow: hidden;
}

.header-actions {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 0 14px 0 6px;
  height: 100%;
  border-left: 1px solid var(--canvas-border);
}

// Annuler / rétablir : un peu plus grands que les boutons « sm » standard.
.header-actions .header-action-btn {
  width: 32px;

  :deep(.bi) {
    font-size: 18px;
  }
}

// L'icône de sauvegarde est posée en ligne (span) : sans ça elle tombe sur la
// ligne de base, plus bas que les autres boutons.
.header-save-reminder {
  position: absolute;
  top: 50%;
  right: calc(100% + 6px);
  transform: translateY(-50%);
}

.header-save {
  display: flex;
  align-items: center;
  transform: translateY(-3px);
}

// Hors du flux et invisibles, mais mises en page avec les mêmes styles.
.legend-probes {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  visibility: hidden;
  pointer-events: none;

  .header-legend {
    width: max-content;
  }
}

.canvas-header-btn {
  border: 1px solid var(--canvas-border);
  background-color: var(--canvas-bg);
  --canvas-btn-hover-bg: rgb(28, 31, 38, 0.2);
}
</style>
